import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { useTranslations } from 'next-intl'
import qs from 'qs'
import { SearchTabQuery, SearchTabQuerySchema } from '~/schemas/searchPageParams'
import { Tabs, TabContent } from '@reward-platform/lift/components/Tabs'
import SearchCarHireForm from '~/components/search-and-results/SearchCarHireForm'
import SearchExperienceForm from '~/components/search-and-results/SearchExperienceForm'
import { SearchFlightFormWrapper } from '~/components/search-and-results/SearchFlightForm/SearchFlightFormWrapper'
import SearchHotelForm from '~/components/search-and-results/SearchHotelForm'
import IntroSection from '~/components/search-and-results/IntroSection'
import { useScrim, ScrimOverlay } from '~/components/shared/Scrim'
import Hero from '~/components/shared/Hero/HeroLegacy'
import { MediaQueryProvider } from '~/context/mediaQuery'
import {
  getTabPropsFromContent,
  useSearchPageContent,
  useSearchPageRouter,
} from '~/hooks/useSearchPage'
import { usePlatformConfig } from '~/context/platform'
import { withPageAuthRequired } from '~/context/auth'
import { isNotEmpty } from '~/utils/typeChecks'
import { RecommendedSection } from '~/components/search-and-results/RecommendedSection'
import { useFeatureFlag } from '~/context/featureFlag'
import FlightAviosOnlyBanner from '~/components/search-and-results/FlightAviosOnlyBanner/FlightAviosOnlyBanner'
import { PromotionalBanners } from '~/components/shared/PromotionalBanners'
import usePromotionBanners from '~/hooks/usePromotionBanners/usePromotionBanners'
import { RichText } from '~/components/contentful/ContentfulRichText'
import { getHeroWithVariant } from '~/components/shared/Hero/HeroLegacy/getHeroWithVariant'
import { getOpCoMetaConfig } from '@reward-platform/platform-config'
import { FLIGHT_FEATURE_FLAGS } from '@reward-platform/featureFlags'
import { datadogRum } from '@datadog/browser-rum'
import { getContentfulImageProps } from '~/utils/assets'
import { isContentfulUrl } from '~/components/shared/ContentfulImage'

// server imports
import { getPlatformBasePathFromRequest } from '@reward-platform/platform-config/server'
import { getAllFeatureVariablesForUser } from '~/server/featureFlags'

import styles from '~/components/search/SearchPage.module.scss'
import {
  ENABLED_FEATURE_PRODUCTS,
  PRODUCT_LEGACY_PATHS,
  getExternalAncillaryUrl,
  shouldUseAerLingusExternalAncillaryLinks,
  useIsSearchPageEnabled,
} from '~/components/search'

datadogRum.setViewName('Search')

const SearchPage = () => {
  const { opCo, buildType, locale } = usePlatformConfig()
  const opCoMeta = getOpCoMetaConfig(opCo)
  const t = useTranslations('pages.search')
  const { isScrimActive, closeScrim, openScrim } = useScrim()

  const { allTabContent = [], getTabContentByProductSlug } = useSearchPageContent()
  const tabSlugs = allTabContent.map((content) => content.product).filter(isNotEmpty)
  const { selectedTab, setSelectedTab, isTabEnabled } = useSearchPageRouter(tabSlugs)
  const { getSlugFromQuery } = useIsSearchPageEnabled()

  const flightsRecommendedEnabled = useFeatureFlag(FLIGHT_FEATURE_FLAGS.flightsRecommendedSection)
  const flightsAviosOnlyEnabled = useFeatureFlag(FLIGHT_FEATURE_FLAGS.flightsAviosOnly)
  const ancillaryTabsAsExternalLinks = useFeatureFlag('ancillaries-tabs-as-external-links')
  const useAerLingusExternalLinks = shouldUseAerLingusExternalAncillaryLinks(
    ancillaryTabsAsExternalLinks,
    opCo,
    buildType
  )

  const { hero, intro, productLabel, recommended, trendingDestinations } =
    getTabContentByProductSlug(selectedTab) ?? {}

  const banners = usePromotionBanners(getSlugFromQuery(selectedTab), 'home')

  const selectedHero = getHeroWithVariant(hero)

  const heroHeading = selectedHero?.heading?.trim()
  const {
    imgSrc: heroImage,
    imgAlt: heroLabel,
    imgContentType: heroImageContentType,
  } = getContentfulImageProps({
    bynderImage: selectedHero?.bynderImage,
    image: selectedHero?.image,
    fallbackAlt: heroHeading,
  })

  const renderSearchComponent = (slug?: SearchTabQuery) => {
    switch (slug) {
      case SearchTabQuerySchema.enum.hotels:
        return (
          <SearchHotelForm
            buttonText={t('hotels.actions.search_button')}
            trendingDestinations={trendingDestinations}
          />
        )
      case SearchTabQuerySchema.enum.carhire:
        return <SearchCarHireForm buttonText={t('car_hire.actions.search_button')} />
      case SearchTabQuerySchema.enum.experiences: {
        const experiencesContent = getTabContentByProductSlug(SearchTabQuerySchema.enum.experiences)
        return (
          <SearchExperienceForm
            buttonText={t('experiences.actions.search_button')}
            contentfulEntryId={experiencesContent?.entryId}
            contentfulRevisionId={experiencesContent?.revisionId}
          />
        )
      }
      case SearchTabQuerySchema.enum.flights:
        return <SearchFlightFormWrapper />

      default:
        return null
    }
  }

  return (
    <MediaQueryProvider>
      {productLabel ? (
        <Head>
          <title>{`${opCoMeta.name} -  ${productLabel} Search`}</title>
        </Head>
      ) : null}
      <Hero
        key={selectedTab}
        src={heroImage}
        alt={heroLabel}
        contentType={heroImageContentType}
        shouldFormatSrc={isContentfulUrl(heroImage)}
      >
        {heroHeading ? <Hero.Headline>{heroHeading}</Hero.Headline> : null}
        {selectedHero?.body ? (
          <Hero.Tagline>
            <RichText document={selectedHero.body} />
          </Hero.Tagline>
        ) : null}
        {banners.length > 0 ? (
          <div className={styles.heroPromotionalBanners}>
            <PromotionalBanners key={selectedTab} banners={banners} isSingleBannerFullWidth />
          </div>
        ) : null}
      </Hero>
      <section className={styles.section}>
        <ScrimOverlay active={isScrimActive} />
        <Tabs label="Search" selectedTab={selectedTab} setSelectedTab={setSelectedTab}>
          {allTabContent
            .map((productContent) => {
              const tabProps = getTabPropsFromContent(
                productContent,
                productContent.product ? isTabEnabled(productContent.product) : true
              )
              const externalAncillaryUrl = getExternalAncillaryUrl(productContent.product, locale)
              const resolvedTabProps =
                tabProps && useAerLingusExternalLinks && externalAncillaryUrl
                  ? {
                      ...tabProps,
                      href: externalAncillaryUrl,
                      variant: 'link',
                    }
                  : tabProps

              return resolvedTabProps ? (
                <TabContent
                  {...resolvedTabProps}
                  onFocus={openScrim}
                  onBlur={closeScrim}
                  key={JSON.stringify(productContent)}
                >
                  {renderSearchComponent(resolvedTabProps.slug as SearchTabQuery)}
                </TabContent>
              ) : null
            })
            .filter(isNotEmpty)}
        </Tabs>
      </section>

      {selectedTab === SearchTabQuerySchema.enum.flights && flightsAviosOnlyEnabled && (
        <FlightAviosOnlyBanner />
      )}
      {flightsRecommendedEnabled && recommended && (
        <RecommendedSection sectionContent={recommended} product={selectedTab} />
      )}
      {intro && <IntroSection sectionContent={intro} product={selectedTab} />}
    </MediaQueryProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { tab, ...searchQuery } = context.query
  const basePath = getPlatformBasePathFromRequest(context.req) ?? ''
  const featureFlagVariableMap = await getAllFeatureVariablesForUser(context.req, context.res)
  const newHomePageEnabled = Boolean(featureFlagVariableMap['homepage-contentful-rebuild']?.value)
  if (newHomePageEnabled) {
    const [slug] = Object.entries(PRODUCT_LEGACY_PATHS).find(
      ([, path]) => path === `/?tab=${tab}`
    ) || ['hotels', null]

    if (ENABLED_FEATURE_PRODUCTS.has(slug as keyof typeof PRODUCT_LEGACY_PATHS)) {
      const queryString = qs.stringify(searchQuery) ? `?${qs.stringify(searchQuery)}` : ''
      return {
        redirect: {
          destination: `${basePath}/${slug}${queryString}`,
          permanent: false,
        },
      }
    }
  }

  return {
    props: {},
  }
}

export default withPageAuthRequired(SearchPage)
